// Project budget parse/format/patch rules (#3787). Pure — no React, no
// Firestore. budgetAmount (exact figure) and budgetRange (preset id) are
// mutually exclusive; `budget` is the display string kept in sync for legacy
// readers, with budgetDisplay as the structured-first read authority.

import { ProjectBudgetRange, ProjectSchemeInterface } from "ts-interfaces";
import { currencySymbol } from "../../../libs/currency";

interface BudgetPatch {
  budget: string;
  budgetAmount: number | null;
  budgetRange: ProjectBudgetRange | null;
}

const BUDGET_RANGES: {
  id: ProjectBudgetRange;
  min: number;
  max: number | null;
}[] = [
  { id: "under-10k", min: 0, max: 10000 },
  { id: "10k-50k", min: 10000, max: 50000 },
  { id: "50k-250k", min: 50000, max: 250000 },
  { id: "250k-plus", min: 250000, max: null },
];

export const BUDGET_RANGE_IDS = BUDGET_RANGES.map((r) => r.id);

const groupFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

/** "$180,000". Not pricing.ts's formatMoney, which renders "US$180,000.00" —
 * a country prefix and cents, both wrong for a budget. */
const formatBudget = (amount: number, currency?: string): string =>
  `${currencySymbol(currency)}${groupFormatter.format(amount)}`;

/** "< $10,000" / "$10,000 – $50,000" / "$250,000+". Currency-aware, which is
 * why the preset is stored as an id and labelled at render. */
export const budgetRangeLabel = (
  id: ProjectBudgetRange,
  currency?: string,
): string => {
  const range = BUDGET_RANGES.find((r) => r.id === id);
  if (!range) return "";
  if (range.max === null) return `${formatBudget(range.min, currency)}+`;
  if (range.min === 0) return `< ${formatBudget(range.max, currency)}`;
  return `${formatBudget(range.min, currency)} – ${formatBudget(
    range.max,
    currency,
  )}`;
};

/** number = store it, null = intentional clear (empty input), false = junk —
 * leave the stored value alone. Not pricing.ts's parsePricingInput: that maps
 * a typed 0 to `undefined`, which fails the whole project save. */
export const parseBudgetInput = (raw: string): number | null | false => {
  const trimmed = (raw || "").trim();
  if (trimmed === "") return null;
  // Reject before the symbol strip below eats the sign and accepts "-5" as 5.
  if (trimmed.includes("-")) return false;
  const digits = trimmed.replace(/[,\s]/g, "").replace(/^[^\d.]+/, "");
  if (!/^\d+(\.\d+)?$/.test(digits)) return false;
  const n = parseFloat(digits);
  if (!Number.isFinite(n)) return false;
  return n;
};

/** Digits (and one decimal point) only — what the input accepts while focused.
 * No live thousands-grouping: inserting separators mid-keystroke jumps the caret. */
export const sanitizeBudgetInput = (raw: string): string =>
  (raw || "").replace(/[^\d.]/g, "");

/** Structured value wins; a legacy free-text string is returned VERBATIM so a
 * pre-#3787 entry round-trips byte-identically until the user edits it. */
export const budgetDisplay = (
  project: Partial<ProjectSchemeInterface>,
): string => {
  if (project?.budgetAmount != null)
    return formatBudget(project.budgetAmount, project.currency);
  if (project?.budgetRange)
    return budgetRangeLabel(project.budgetRange, project.currency);
  return project?.budget || "";
};

/** Edit-time prefill as raw digits: a legacy string with digits normalizes,
 * one without ("dsfdsafds") yields "". */
export const budgetEditValue = (
  project: Partial<ProjectSchemeInterface>,
): string => {
  if (project?.budgetAmount != null) return String(project.budgetAmount);
  if (project?.budgetRange) return "";
  const parsed = parseBudgetInput(project?.budget || "");
  return typeof parsed === "number" ? String(parsed) : "";
};

/** The amount/range mutual-exclusion invariant lives here. Always emits all
 * three keys and never `undefined` (cleared = null) — project saves are a
 * whole-doc setDoc(merge) that rejects undefined and fails the ENTIRE save. */
export const budgetPatchFromAmount = (
  amount: number | null,
  currency?: string,
): BudgetPatch => ({
  budget: amount == null ? "" : formatBudget(amount, currency),
  budgetAmount: amount,
  budgetRange: null,
});

export const budgetPatchFromRange = (
  id: ProjectBudgetRange,
  currency?: string,
): BudgetPatch => ({
  budget: budgetRangeLabel(id, currency),
  budgetAmount: null,
  budgetRange: id,
});

/** The full currency-switch patch: the new currency plus, when a structured
 * budget exists, its re-rendered display string — so a doc never stores
 * "$180,000" alongside currency: "EUR". One rule for every editor (#3787). */
export const currencyPatch = (
  project: Partial<ProjectSchemeInterface>,
  currency: string,
): Partial<ProjectSchemeInterface> => {
  if (project?.budgetAmount != null)
    return { currency, budget: formatBudget(project.budgetAmount, currency) };
  if (project?.budgetRange)
    return {
      currency,
      budget: budgetRangeLabel(project.budgetRange, currency),
    };
  return { currency };
};
